<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import {
  AdminFilterBar,
  AdminDataTable,
  AdminDrawer,
  RrInput,
  RrSelect,
  RrButton,
  RrForm,
  RrFormItem,
  RrCheckbox,
  RrTag
} from '@xjlv2/admin-layout'
import type {
  CreateRoleRequest,
  DataScopeGrant,
  Dept,
  Role,
  TenantMenuGroup,
  UpdateRoleRequest
} from '../../types'

type ScopeType = DataScopeGrant['scopeType']
type DraftMode = 'create' | 'edit'

interface RoleDraft {
  mode: DraftMode
  roleId: number | null
  code: string
  name: string
  menuCodes: string[]
  permissions: string[]
  dataScopes: Record<string, { scopeType: ScopeType; customDeptIds: number[] }>
}

const props = defineProps<{
  roles: Role[]
  menuGroups: TenantMenuGroup[]
  depts: Dept[]
  permissions: string[]
  dataResources: Array<{ code: string; tableName?: string }>
  canCreate: boolean
  canUpdate: boolean
  loading: boolean
}>()

const emit = defineEmits<{
  create: [payload: CreateRoleRequest]
  update: [roleId: number, payload: UpdateRoleRequest]
}>()

const showDraftForm = ref(false)
const filters = ref({
  keyword: '',
  dataScopeType: 'ALL' as 'ALL' | ScopeType
})
const draft = ref<RoleDraft>(emptyDraft())

const roleRows = computed(() =>
  props.roles.map((role) => ({
    ...role,
    menuNames: props.menuGroups
      .flatMap((group) => group.menus)
      .filter((menu) => role.menuCodes.includes(menu.menuCode))
      .map((menu) => menu.name),
    dataScopeTypes: Array.from(new Set(role.dataScopes.map((scope) => scope.scopeType))),
    dataScopeSummary: role.dataScopes.map((scope) => `${scope.resourceCode}:${dataScopeText(scope.scopeType)}`)
  }))
)

const page = ref(1)
const pageSize = ref(20)

const filteredRoles = computed(() => {
  const keyword = filters.value.keyword.trim().toLowerCase()
  return roleRows.value.filter((role) => {
    const matchesKeyword =
      keyword.length === 0 ||
      role.name.toLowerCase().includes(keyword) ||
      role.code.toLowerCase().includes(keyword) ||
      role.menuCodes.some((menuCode) => menuCode.toLowerCase().includes(keyword)) ||
      role.permissions.some((permission) => permission.toLowerCase().includes(keyword))
    const matchesDataScope =
      filters.value.dataScopeType === 'ALL' || role.dataScopeTypes.includes(filters.value.dataScopeType)
    return matchesKeyword && matchesDataScope
  })
})

const paginatedRoles = computed(() => {
  const start = (page.value - 1) * pageSize.value
  return filteredRoles.value.slice(start, start + pageSize.value)
})

watch([() => filters.value.keyword, () => filters.value.dataScopeType], () => {
  page.value = 1
})

const permissionGroups = computed(() => {
  const groups = new Map<string, string[]>()
  for (const permission of props.permissions) {
    const source = permission.startsWith('system:')
      ? 'SaaS System'
      : permission.startsWith('crm:')
        ? 'CRM'
        : permission.startsWith('erp:')
          ? 'ERP'
          : 'Module'
    const items = groups.get(source) ?? []
    items.push(permission)
    groups.set(source, items)
  }
  return Array.from(groups.entries()).map(([source, permissions]) => ({
    source,
    permissions
  }))
})

const selectedMenuPermissions = computed(() =>
  props.menuGroups
    .flatMap((group) => group.menus)
    .filter((menu) => draft.value.menuCodes.includes(menu.menuCode))
    .map((menu) => menu.permission)
)

const effectiveDraftPermissions = computed(() =>
  Array.from(new Set([...draft.value.permissions, ...selectedMenuPermissions.value])).sort()
)

const draftDataScopes = computed<DataScopeGrant[]>(() =>
  props.dataResources.map((resource) => {
    const item = draft.value.dataScopes[resource.code] ?? { scopeType: 'SELF' as ScopeType, customDeptIds: [] }
    return {
      resourceCode: resource.code,
      scopeType: item.scopeType,
      customDeptIds: item.scopeType === 'CUSTOM_DEPT' ? item.customDeptIds : []
    }
  })
)

function emptyDraft(): RoleDraft {
  return {
    mode: 'create',
    roleId: null,
    code: 'crm_operator',
    name: 'CRM 运营',
    menuCodes: [],
    permissions: [],
    dataScopes: {}
  }
}

function openCreateForm() {
  draft.value = emptyDraft()
  showDraftForm.value = true
}

function openEditForm(role: Role) {
  draft.value = {
    mode: 'edit',
    roleId: role.id,
    code: role.code,
    name: role.name,
    menuCodes: [...role.menuCodes].sort(),
    permissions: role.permissions
      .filter((permission) => !props.menuGroups.flatMap((group) => group.menus).some((menu) => menu.permission === permission))
      .sort(),
    dataScopes: Object.fromEntries(
      role.dataScopes.map((scope) => [
        scope.resourceCode,
        {
          scopeType: scope.scopeType,
          customDeptIds: [...scope.customDeptIds].sort((left, right) => left - right)
        }
      ])
    )
  }
  for (const resource of props.dataResources) {
    if (!draft.value.dataScopes[resource.code]) {
      draft.value.dataScopes[resource.code] = {
        scopeType: 'SELF',
        customDeptIds: []
      }
    }
  }
  showDraftForm.value = true
}

function closeDraftForm() {
  showDraftForm.value = false
  draft.value = emptyDraft()
}

function togglePermission(permission: string) {
  const selected = new Set(draft.value.permissions)
  if (selected.has(permission)) selected.delete(permission)
  else selected.add(permission)
  draft.value.permissions = Array.from(selected).sort()
}

function toggleMenu(menuCode: string) {
  const selected = new Set(draft.value.menuCodes)
  if (selected.has(menuCode)) selected.delete(menuCode)
  else selected.add(menuCode)
  draft.value.menuCodes = Array.from(selected).sort()
}

function setResourceScope(resourceCode: string, scopeType: ScopeType) {
  const current = draft.value.dataScopes[resourceCode] ?? { scopeType: 'SELF' as ScopeType, customDeptIds: [] }
  draft.value.dataScopes = {
    ...draft.value.dataScopes,
    [resourceCode]: {
      scopeType,
      customDeptIds: scopeType === 'CUSTOM_DEPT' ? current.customDeptIds : []
    }
  }
}

function handleResourceScopeChange(resourceCode: string, value: string) {
  setResourceScope(resourceCode, value as ScopeType)
}

function toggleResourceDept(resourceCode: string, deptId: number) {
  const current = draft.value.dataScopes[resourceCode] ?? { scopeType: 'CUSTOM_DEPT' as ScopeType, customDeptIds: [] }
  const selected = new Set(current.customDeptIds)
  if (selected.has(deptId)) selected.delete(deptId)
  else selected.add(deptId)
  draft.value.dataScopes = {
    ...draft.value.dataScopes,
    [resourceCode]: {
      scopeType: 'CUSTOM_DEPT',
      customDeptIds: Array.from(selected).sort((left, right) => left - right)
    }
  }
}

function resetFilters() {
  filters.value = {
    keyword: '',
    dataScopeType: 'ALL'
  }
}

function dataScopeText(scopeType: string) {
  const labels: Record<string, string> = {
    ALL: '全部数据',
    DEPT: '本部门',
    DEPT_AND_CHILD: '本部门及下级',
    SELF: '仅本人',
    CUSTOM_DEPT: '指定部门',
    CUSTOM_RULE: '自定义规则'
  }
  return labels[scopeType] ?? scopeType
}

function submitDraft() {
  if (!draft.value.name || (draft.value.mode === 'create' && !draft.value.code)) return
  const payload = {
    name: draft.value.name,
    menuCodes: draft.value.menuCodes,
    permissions: effectiveDraftPermissions.value,
    dataScopes: draftDataScopes.value
  }
  if (draft.value.mode === 'create') {
    if (!props.canCreate) return
    emit('create', {
      code: draft.value.code,
      ...payload
    })
  } else if (draft.value.roleId != null) {
    if (!props.canUpdate) return
    emit('update', draft.value.roleId, payload)
  }
  closeDraftForm()
}
</script>

<template>
  <section class="panel span management-list">
    <div class="panel-head">
      <div>
        <p class="eyebrow">Role Management</p>
        <h2>角色列表</h2>
      </div>
      <span>{{ filteredRoles.length }} / {{ roles.length }} 个角色</span>
    </div>

    <AdminFilterBar :loading="loading" @reset="resetFilters">
      <RrInput v-model:value.trim="filters.keyword" placeholder="角色、编码、菜单、权限" />
      <RrSelect v-model:value="filters.dataScopeType" :options="[
        { label: '全部范围', value: 'ALL' },
        { label: '本部门', value: 'DEPT' },
        { label: '本部门及下级', value: 'DEPT_AND_CHILD' },
        { label: '仅本人', value: 'SELF' },
        { label: '指定部门', value: 'CUSTOM_DEPT' }
      ]" />
      <template #actions>
        <RrButton @click="resetFilters" :disabled="loading">重置</RrButton>
        <RrButton type="primary" :disabled="!canCreate || loading" @click="openCreateForm">新增角色</RrButton>
      </template>
    </AdminFilterBar>

    <AdminDrawer
      v-model:open="showDraftForm"
      :title="draft.mode === 'create' ? '新增角色' : '编辑角色'"
      :width="600"
      @close="closeDraftForm"
    >
      <RrForm :disabled="loading || (draft.mode === 'create' ? !canCreate : !canUpdate)">
        <RrFormItem label="角色编码">
          <RrInput v-model:value.trim="draft.code" :disabled="draft.mode === 'edit'" />
        </RrFormItem>
        <RrFormItem label="角色名称">
          <RrInput v-model:value.trim="draft.name" />
        </RrFormItem>
        <RrFormItem label="授权结果">
          <RrInput :value="`${draft.menuCodes.length} 个菜单，${effectiveDraftPermissions.length} 个 API 权限`" disabled />
        </RrFormItem>
      </RrForm>

      <div class="permission-config-grid">
        <div class="permission-config-block">
          <div class="section-head">
            <h3>菜单权限</h3>
            <span>{{ draft.menuCodes.length }} 已选</span>
          </div>
          <div class="check-grid compact-check-grid">
            <div v-for="group in menuGroups" :key="group.moduleCode" class="permission-group">
              <h3>{{ group.moduleName }}</h3>
              <!-- RrCheckbox 自带 label，外层使用 div 避免嵌套 label 被浏览器重排。 -->
              <div v-for="menu in group.menus" :key="menu.menuCode" class="check-row">
                <RrCheckbox
                  :checked="draft.menuCodes.includes(menu.menuCode)"
                  :disabled="loading || (draft.mode === 'create' ? !canCreate : !canUpdate)"
                  @change="toggleMenu(menu.menuCode)"
                />
                <span>
                  <strong>{{ menu.name }}</strong>
                  <small>{{ menu.menuCode }} · {{ menu.permission }}</small>
                </span>
              </div>
            </div>
            <p v-if="menuGroups.length === 0" class="empty-state">没有可授权的菜单。</p>
          </div>
        </div>

        <div class="permission-config-block">
          <div class="section-head">
            <h3>API 权限</h3>
            <span>{{ effectiveDraftPermissions.length }} 生效</span>
          </div>
          <div class="check-grid compact-check-grid">
            <div v-for="group in permissionGroups" :key="group.source" class="permission-group">
              <h3>{{ group.source }}</h3>
              <div v-for="permission in group.permissions" :key="permission" class="check-row">
                <RrCheckbox
                  :checked="draft.permissions.includes(permission) || selectedMenuPermissions.includes(permission)"
                  :disabled="
                    loading ||
                    selectedMenuPermissions.includes(permission) ||
                    (draft.mode === 'create' ? !canCreate : !canUpdate)
                  "
                  @change="togglePermission(permission)"
                />
                <span>{{ permission }}</span>
              </div>
            </div>
            <p v-if="permissions.length === 0" class="empty-state">没有可授权的权限点。</p>
          </div>
        </div>
      </div>

      <div class="permission-config-block">
        <div class="section-head">
          <h3>数据权限</h3>
          <span>{{ dataResources.length }} 个数据资源</span>
        </div>
        <div class="data-scope-editor">
          <div v-for="resource in dataResources" :key="resource.code" class="data-scope-card">
            <div class="section-head">
              <div>
                <h3>{{ resource.code }}</h3>
                <span>{{ resource.tableName ?? '业务数据表' }}</span>
              </div>
            </div>
            <label>
              数据范围
              <RrSelect
                :value="draft.dataScopes[resource.code]?.scopeType ?? 'SELF'"
                :disabled="loading || (draft.mode === 'create' ? !canCreate : !canUpdate)"
                :options="[
                  { label: '全部数据', value: 'ALL' },
                  { label: '本部门', value: 'DEPT' },
                  { label: '本部门及下级', value: 'DEPT_AND_CHILD' },
                  { label: '仅本人', value: 'SELF' },
                  { label: '指定部门', value: 'CUSTOM_DEPT' }
                ]"
                @change="handleResourceScopeChange(resource.code, $event)"
              />
            </label>
            <div
              v-if="(draft.dataScopes[resource.code]?.scopeType ?? 'SELF') === 'CUSTOM_DEPT'"
              class="check-grid compact-check-grid"
            >
              <div v-for="dept in depts" :key="dept.id" class="check-row">
                <RrCheckbox
                  :checked="draft.dataScopes[resource.code]?.customDeptIds.includes(dept.id)"
                  :disabled="loading || (draft.mode === 'create' ? !canCreate : !canUpdate)"
                  @change="toggleResourceDept(resource.code, dept.id)"
                />
                <span>{{ dept.name }} · {{ dept.code }}</span>
              </div>
              <p v-if="depts.length === 0" class="empty-state">没有可选择的部门。</p>
            </div>
          </div>
          <p v-if="dataResources.length === 0" class="empty-state">没有可配置的数据资源。</p>
        </div>
      </div>

      <template #footer>
        <RrButton @click="closeDraftForm">取消</RrButton>
        <RrButton type="primary" :disabled="loading || (draft.mode === 'create' ? !canCreate : !canUpdate)" @click="submitDraft">
          {{ draft.mode === 'create' ? '创建角色' : '保存角色' }}
        </RrButton>
      </template>
    </AdminDrawer>

    <AdminDataTable
      :columns="[
        { key: 'role', title: '角色' },
        { key: 'menus', title: '菜单' },
        { key: 'permissions', title: 'API 权限' },
        { key: 'dataScopes', title: '数据范围' },
        { key: 'actions', title: '操作', width: 100, align: 'right' }
      ]"
      :data-source="paginatedRoles"
      :loading="loading"
      :current="page"
      :page-size="pageSize"
      :total="filteredRoles.length"
      row-key="id"
      :count="filteredRoles.length"
      :empty="filteredRoles.length === 0"
      empty-title="暂无匹配角色"
      @update:current="page = $event"
      @update:page-size="pageSize = $event"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'role'">
          <span>
            <strong>{{ record.name }}</strong>
            <br />
            <small style="color: var(--rr-color-text-muted);">{{ record.code }} · ID {{ record.id }}</small>
          </span>
        </template>
        <template v-else-if="column.key === 'menus'">
          <div style="display: flex; gap: 4px; flex-wrap: wrap;">
            <RrTag v-for="menu in record.menuNames.slice(0, 4)" :key="menu">{{ menu }}</RrTag>
            <RrTag v-if="record.menuNames.length > 4" type="info">+{{ record.menuNames.length - 4 }}</RrTag>
            <RrTag v-if="record.menuNames.length === 0" type="info">无菜单</RrTag>
          </div>
        </template>
        <template v-else-if="column.key === 'permissions'">
          {{ record.permissions.length }} 个权限
        </template>
        <template v-else-if="column.key === 'dataScopes'">
          <div style="display: flex; gap: 4px; flex-wrap: wrap;">
            <RrTag v-for="scope in record.dataScopeSummary.slice(0, 3)" :key="scope">{{ scope }}</RrTag>
            <RrTag v-if="record.dataScopeSummary.length > 3" type="info">+{{ record.dataScopeSummary.length - 3 }}</RrTag>
            <RrTag v-if="record.dataScopeTypes.length === 0" type="info">未配置</RrTag>
          </div>
        </template>
        <template v-else-if="column.key === 'actions'">
          <RrButton type="link" :disabled="!canUpdate || loading" @click="openEditForm(record)">
            编辑
          </RrButton>
        </template>
      </template>
    </AdminDataTable>
  </section>
</template>
